import { inject, Injectable, Type } from '@angular/core';

import { Activity } from './activity.model';
import { BeehiveApiService } from '../beehive-api.service';
import { Application } from '../application/application.model';
import { Workshop } from '../workshop/workshop.model';
import { Course } from '../course/course.model';
import { Camp } from '../camp/camp.model';
import { isApplication } from '../application/lib/application.util';
import { filter, Observable, switchMap, tap } from 'rxjs';
import { CreateActivityDialogComponent } from '../../backoffice/admin/activity/create-activity-dialog/create-activity-dialog.component';
import { CreateActivityData } from '../../backoffice/admin/activity/create-activity-dialog/create-activity.interfaces';
import { ActivityData } from '../application/lib/application.interfaces';
import { MatDialog } from '@angular/material/dialog';
import { ApplicationService } from '../application/application.service';
import { ApiQueryParams } from '../api.operators';
import { CourseTheme } from '../course-theme/course-theme.model';
import { WorkshopTheme } from '../workshop-theme/workshop-theme.model';
import { ApiResource } from '../api-resource';
import { Schedule } from '../_lib/schedule';
import { Staff } from '../staff/staff.model';
import { findStaffScheduleOverlap } from '../../util/target.util';

@Injectable({ providedIn: 'root' })
export class ActivityService extends BeehiveApiService<Activity> {
  protected entityType = Activity;
  protected endpoint = 'activities';
  protected readonly dialog = inject(MatDialog);
  protected readonly applicationService = inject(ApplicationService);

  openCreateDialog(
    applicationOrTarget: Application | Workshop | Course | Camp,
  ) {
    const application = isApplication(applicationOrTarget)
      ? applicationOrTarget
      : null;
    const target = isApplication(applicationOrTarget)
      ? applicationOrTarget.target
      : applicationOrTarget;

    return this.applicationService.getOpenSpots(target).pipe(
      switchMap((openSpots) => {
        const ref = this.dialog.open(CreateActivityDialogComponent, {
          data: {
            target: target,
            ...(application
              ? {
                  staff: application.staff,
                  appliedRoles: application.roles,
                }
              : {}),
            openSpots: openSpots.openLabels,
          } as const satisfies CreateActivityData,
        });
        return ref.afterClosed();
      }),
      filter(
        (
          data,
        ): data is {
          activityData: ActivityData;
          mainActivity?: Activity;
          overlappingActivities: Activity[];
        } => !!data,
      ),
    );
  }

  override list(params?: ApiQueryParams<Activity>): Observable<Activity[]> {
    return super
      .list(params)
      .pipe(tap((activities) => activities.forEach(patchActivity)));
  }

  override read(
    id: string,
    rawParams?: ApiQueryParams<Activity>,
  ): Observable<Activity> {
    return super.read(id, rawParams).pipe(tap(patchActivity));
  }

  protected override findOne<E extends ApiResource<any> = Activity>(
    path: string[],
    params: ApiQueryParams<E> | undefined,
    type: Type<E>,
  ): Observable<E | null> {
    return super
      .findOne(path, params, type)
      .pipe(
        tap(
          (r) =>
            ((type as any) === Activity && r
              ? patchActivity(r as any)
              : null) as E | null,
        ),
      );
  }

  getStaffOverlappingActivities(
    staff: Staff,
    schedule: Schedule,
  ): Observable<Activity[]> {
    return this.list({ filters: { 'staff.id': staff.id } });
  }

  findOverlappingActivities(
    activity: Activity,
    rawParams?: ApiQueryParams<Activity>,
  ) {
    return findStaffScheduleOverlap(this, activity.staff, activity, rawParams);
  }
}

export function patchActivity(activity: Activity) {
  if (activity.workshop) {
    activity.workshop = new Workshop(activity.workshop);
    activity.workshop.theme = new WorkshopTheme(activity.workshop.theme);
  }
  if (activity.course) {
    activity.course = new Course(activity.course);
    activity.course.theme = new CourseTheme(activity.course.theme);
  }
  if (activity.camp) activity.camp = new Camp(activity.camp);
  activity.staff = new Staff(activity.staff);
}
